import { Alert, Col, Flex, Form, Typography, Spin } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";

import { CustomButton } from "ui";
import { createUser, getCompanyById } from "services";
import { dateISOFormat, getPhoneMaskValue, isValidDateRange, parseErrorMessage } from "utils";
import { routes } from "constants/routes";
import { components } from "types/openapi";
import { UserForm } from "components";
import { useNotificationContext } from "providers";
import { NOTIFICATION_TYPE_ENUM } from "interfaces";

type FieldType = components["schemas"]["CreateUserRequest"];
type CompanyEntity = components["schemas"]["CompanyDto"];

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

export const UserCreatePage = () => {
  const query = useQuery();
  const companyId = query.get("companyId");
  const navigate = useNavigate();
  const { openNotification } = useNotificationContext();

  const [loading, setLoading] = useState(false);
  const [company, setCompany] = useState<CompanyEntity | null>(null);
  const [loadingCompany, setLoadingCompany] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    if (companyId) {
      setLoadingCompany(true);
      getCompanyById({ id: companyId })
        .then((companyData) => {
          setCompany(companyData);
        })
        .catch((error) => {
          openNotification({
            description: "Ошибка при загрузке данных компании",
            type: NOTIFICATION_TYPE_ENUM.ERROR,
          });
        })
        .finally(() => {
          setLoadingCompany(false);
        });
    }
  }, [companyId, openNotification]);

  const cancelCreating = () => {
    return Promise.resolve(navigate(`/${routes.companies}`));
  };

  const closeResultModal = (id: string) => {
    return Promise.resolve(navigate(`/${routes.users}/${id}`));
  };

  const saveUser = async () => {
    setLoading(true);
    const values = form.getFieldsValue();
    const dataToSend: FieldType = {
      ...values,
      companyId,
      phone: getPhoneMaskValue(values.phone),
      attorneyDate: isValidDateRange(values.attorneyDate) ? dateISOFormat(values.attorneyDate[0], false) : "",
      attorneyExpirationDate: isValidDateRange(values.attorneyDate) ? dateISOFormat(values.attorneyDate[1], false) : "",
    };

    await createUser(dataToSend)
      .then((id) => {
        openNotification({
          message: "Пользователь зарегистрирован",
          type: NOTIFICATION_TYPE_ENUM.SUCCESS,
        });
        setTimeout(() => {
          closeResultModal(id);
          setLoading(false);
        }, 1500);
      })
      .catch((e) => {
        openNotification({
          description: parseErrorMessage(e),
          type: NOTIFICATION_TYPE_ENUM.ERROR,
        });
        setLoading(false);
      });
  };

  if (!companyId) {
    return (
      <Flex vertical gap="large">
        <Alert
          message={"Ошибка"}
          description={"Для создания пользователя перейдите в карточку компании"}
          type={"error"}
          showIcon
        />
        <Col>
          <CustomButton htmlType="button" onClick={cancelCreating}>
            Ок
          </CustomButton>
        </Col>
      </Flex>
    );
  }

  return (
    <Spin spinning={loadingCompany}>
      <Flex vertical>
        <Typography.Title>Добавить пользователя</Typography.Title>
        <UserForm
          form={form}
          disabled={loading}
          closeHandler={cancelCreating}
          onFinish={saveUser}
          companyRole={company?.role}
        />
      </Flex>
    </Spin>
  );
};
